import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  RESPONSE_INIT,
  ChangeDetectionStrategy,
} from '@angular/core';
import { indexPagePath } from '@helpers/indexPagePath';
import { EnvToken, SeoSegmentsToken } from '@app/core/env.tokens';
import { LocalServicePageService } from '@services/http/LocalServicePage.service';
import { FooterComponent } from '@components/shared/footer/footer.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TitleCasePipe } from '@angular/common';
import { BreadcrumbListComponent } from '@components/shared/breadcrumb-list/breadcrumb-list.component';
import { catchError, Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LocalServiceModel } from '@models/localService/localService.model';
import { TranslateService } from '@services/logic/translate.service';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { SEOService } from '@services/logic/seo.service';
import {
  localServicesIndexDescription,
  localServicesIndexTitle,
} from '../local-services-seo-copy';

/**
 * Below this many companies the index is thin content, so it noindexes itself. The
 * keep-list prune leaves most categories with a handful of survivors or none.
 */
export const MIN_INDEXABLE_ENTRIES = 5;

@Component({
  selector: 'app-local-services-index',
  imports: [
    FooterComponent,
    RouterLink,
    BreadcrumbListComponent,
    TitleCasePipe,
    TranslatePipe,
  ],
  // TranslatePipe is provided, not just imported: the class is injected in the
  // constructor for schema names, and `imports` only makes a pipe available to
  // the template. Same pattern as aboutUs.component.ts.
  providers: [SEOService, TranslatePipe],
  templateUrl: './localServicesIndex.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../../tips-index/tips-index.component.scss',
})
export class LocalServicesIndexComponent implements OnInit, OnDestroy {
  protected readonly env = inject(EnvToken);
  protected readonly seg = inject(SeoSegmentsToken);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly seoService = inject(SEOService);

  private readonly responseInit = inject(RESPONSE_INIT, { optional: true });
  public localServices: LocalServiceModel[] = [];
  private destroy$: Subject<void> = new Subject<void>();
  mainCategory: string = '';
  subCategory: string = '';
  subSubCategory: string = '';
  hasHeadCategory: boolean = false;
  hasSubCategory: boolean = false;
  hasSubSubCategory: boolean = false;
  activeCategory: string = '';
  basePath: string = '';
  partsOfUrl: { name: string; url: string }[] = [];

  mainCategoryTitle: string = '';
  subCategoryTitle: string = '';
  subSubCategoryTitle: string = '';
  activeCategoryTitle: string = '';

  public breadCrumSchema: any;
  loading: boolean = true;

  constructor(
    private localServicesService: LocalServicePageService,
    private activatedRoute: ActivatedRoute,
    private route: Router,
    private translate: TranslateService,
    private translatePipe: TranslatePipe,
  ) {}

  ngOnInit() {
    this.findCategoryForIndex();
    this.createBasePath();
    // Baseline head tags before the fetch settles, so even an error shell
    // carries a real title/description/canonical instead of the bare brand
    // fallback this template shipped for years (same defect as /affald).
    this.setSeoMeta();
    // Kicked last: the subscribe callback re-emits the head tags with the
    // company count and needs activeCategoryTitle to exist by then.
    if (this.activeCategory) {
      this.fetchLocalServicesByAsCat(this.activeCategory);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private findCategoryForIndex() {
    const headCategory = this.activatedRoute.snapshot.params['headCategory'];
    const subCategory = this.activatedRoute.snapshot.params['subCategory'];
    const subSubCategory =
      this.activatedRoute.snapshot.params['subSubCategory'];

    if (headCategory) {
      this.mainCategory = headCategory;
      this.hasHeadCategory = true;
    }
    if (subCategory) {
      this.subCategory = subCategory;
      this.hasSubCategory = true;
    }
    if (subSubCategory) {
      this.subSubCategory = subSubCategory;
      this.hasSubSubCategory = true;
    }

    // Deepest available category: this must match the hub's link gate
    // (checkLocalServicesAvailability counts by subSub || sub || head), or a
    // three-level index URL would list and describe its parent category.
    this.activeCategory = subSubCategory || subCategory || headCategory;
  }

  private createBasePath(): void {
    let basePath = this.mainCategory;
    if (this.hasSubCategory) {
      basePath += '/' + this.subCategory;
    }
    if (this.hasSubSubCategory) {
      basePath += '/' + this.subSubCategory;
    }
    this.basePath = basePath;
    this.setBreadCrumb();
  }

  private fetchLocalServicesByAsCat(categorySlug: string) {
    this.localServicesService
      .fetchLocalServicePageByCategorySlug(categorySlug)
      .pipe(
        takeUntil(this.destroy$),
        catchError((err: any): Observable<never[]> => {
          // Only a genuine 404 is a cacheable not-found. Transient upstream
          // failures (timeout/status 0, 429, 5xx) must NOT become a permanently
          // cached 404 — emit a retryable 503 instead (see server.ts).
          if (err?.status === 404) {
            if (this.responseInit) {
              this.responseInit.status = 404;
            }
            this.route.navigate(['/404'], { skipLocationChange: true });
          } else if (this.responseInit) {
            this.responseInit.status = 503;
          }
          return of([]);
        }),
      )
      .subscribe((data: any[]) => {
        this.localServices = data.sort(
          (a, b): number => b.viewCount - a.viewCount,
        );
        this.loading = false;
        // Re-emit with the company count now that it is known — the count is
        // what makes each directory's description its own.
        this.setSeoMeta(this.localServices.length);
        this.applyIndexability();
        this.cdr.markForCheck();
      });
  }

  /**
   * Directory-style head tags built from the route's category. The canonical
   * is deliberately omitted so SEOService self-canonicalizes from the routed
   * URL — this template answers under more than one category chain
   * (`/vand/…` and `/vvs-arbejde/vand/…`), and recomputing a URL here would
   * repeat the three-implementations trap documented in url-generation.md.
   */
  private setSeoMeta(companyCount?: number): void {
    this.seoService.setPageMeta({
      metaTitle: localServicesIndexTitle(this.activeCategoryTitle),
      metaDesc: localServicesIndexDescription(
        this.activeCategoryTitle,
        companyCount,
      ),
      appendSiteName: false,
    });
  }

  /** Noindexes the page when it lists too few companies to be worth indexing. */
  public applyIndexability(): void {
    this.seoService.setRobotsNoindex(
      this.localServices.length < MIN_INDEXABLE_ENTRIES,
    );
  }

  private setBreadCrumb(): void {
    this.mainCategoryTitle = this.transformCategoryToReadableText(
      this.mainCategory,
    );
    this.subCategoryTitle = this.transformCategoryToReadableText(
      this.subCategory,
    );
    this.subSubCategoryTitle = this.transformCategoryToReadableText(
      this.subSubCategory,
    );
    this.activeCategoryTitle = this.transformCategoryToReadableText(
      this.activeCategory,
    );

    if (this.mainCategory) {
      this.partsOfUrl.push({
        name: this.mainCategoryTitle ?? this.mainCategory,
        url: `${this.env.domain}/${this.mainCategory}/${this.seg.suffix.hubIndex}`,
      });
    }
    if (this.mainCategory && this.subCategory && !this.subSubCategory) {
      this.partsOfUrl.push({
        name: this.subCategoryTitle,
        url: `${this.env.domain}/${this.mainCategory}/${this.subCategory}/${this.seg.suffix.hubIndex}`,
      });
    } else if (this.subSubCategory && this.subCategory && this.mainCategory) {
      this.partsOfUrl.push({
        name: this.subSubCategoryTitle ?? this.subCategory,
        url: `${this.env.domain}/${this.mainCategory}/${this.subCategory}/${this.subSubCategory}/${this.seg.suffix.hubIndex}`,
      });
    }
    if (this.activeCategory) {
      this.partsOfUrl.push({
        name: this.translatePipe.transform(
          'SEO.SCHEMA.LOCAL_SERVICES_INDEX_NAME',
        ),
        // Full chain, not the deepest slug alone: `/vand/local-service-index`
        // is a routable variant whose own breadcrumb links `/vand/hub` (503).
        url: `${this.env.domain}${indexPagePath(
          this.seg.indexPage.localServices,
          [this.mainCategory, this.subCategory, this.subSubCategory],
        )}`,
      });
    }
    this.injectBreadCrumbSchema();
  }

  private injectBreadCrumbSchema() {
    this.breadCrumSchema = {
      '@context': 'http://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [],
    };
    this.partsOfUrl.forEach(
      (e: { name: string; url: string }, i: number): void => {
        this.breadCrumSchema.itemListElement.push({
          '@type': 'ListItem',
          // Google requires 1-based breadcrumb positions
          position: i + 1,
          item: {
            '@id': e.url,
            name: e.name,
          },
        });
      },
    );
  }

  public generateLocalServiceLink(serviceSlug: string): string {
    return `/${this.basePath}/${this.seg.suffix.hubIndex}/${serviceSlug}/${this.seg.suffix.localServiceCompany}`;
  }

  transformCategoryToReadableText(category: string | undefined): string {
    if (!category) return '';
    let readableText = category;
    readableText = readableText.replace(/-/g, ' ');
    readableText = readableText.replace(/oe/g, 'ø');
    readableText = readableText.replace(/aa/g, 'å');
    readableText = readableText.replace(/ae/g, 'æ');
    return readableText;
  }

  protected readonly environment = this.env;
}
